import { FetchResult } from '@apollo/client';
import { isEmpty, values } from 'lodash';
import { useCallback } from 'react';

import { useReportError } from '@/hooks/useReportError';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import {
  DismissAidpSuggestionsMutation,
  useDismissAidpSuggestionsMutation,
} from '@/modules/aiDP/graphql/aiDP.generated';
import { AiSuggestionAcceptanceStatus } from '@/types/schema';

interface UseDismissAIDPSuggestionsResult {
  dismissSuggestions: () => Promise<
    FetchResult<DismissAidpSuggestionsMutation> | undefined
  >;
  isDismissing: boolean;
}

/**
 * Hook to handle dismissing AI DP suggestions without updating the entity.
 * Acknowledges all unacknowledged suggestions as rejected.
 */
export function useDismissAIDPSuggestions(): UseDismissAIDPSuggestionsResult {
  const { reportError } = useReportError();

  const { suggestionsByKind } = useDispositiveProvisionSuggestionsContext();

  const [dismissMutation, { loading: isDismissing }] =
    useDismissAidpSuggestionsMutation({
      onError: (error) =>
        reportError('Failed to dismiss AI DP suggestions', error),
    });

  const dismissSuggestions = useCallback(async () => {
    // Create the input to acknowledge suggestions as rejected
    const acknowledgeSuggestionsInput = values(suggestionsByKind)
      .flat()
      .filter((suggestion) => suggestion.acceptanceStatus === null)
      .map((suggestion) => ({
        suggestionID: suggestion.id,
        status: AiSuggestionAcceptanceStatus.Rejected,
        // We don't set matched IDs for rejected suggestions
      }));

    if (isEmpty(acknowledgeSuggestionsInput)) {
      return;
    }

    return dismissMutation({
      variables: {
        acknowledgeSuggestionsInput,
      },
    });
  }, [dismissMutation, suggestionsByKind]);

  return {
    dismissSuggestions,
    isDismissing,
  };
}
